import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Dropdown,
  DropdownButton,
  Alert,
  Button
} from 'react-bootstrap';
import CarManagmentProvider, {
  useCarManagmentContext
} from 'providers/CarManagmentProvider';
import SkeletonLoader from 'components/base/skeletonLoader';

// Custom CSS to override Bootstrap's dropdown styles
const dropdownStyles = `
  .custom-dropdown .dropdown-toggle {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    min-width: 140px;
    width: 140px;
  }
  .custom-dropdown .dropdown-toggle::after {
    display: none !important;
  }
  .custom-sort-button {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    margin-left: -1px !important;
  }
  .custom-dropdown .dropdown-menu {
    min-width: 140px;
  }
`;

const index = () => {
  return (
    <CarManagmentProvider>
      <CarStateManagement />
    </CarManagmentProvider>
  );
};

const CarStateManagement = () => {
  const { carsMaintenance, maintenanceEventsByCar } = useCarManagmentContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<
    | 'carPlate'
    | 'marca'
    | 'modelo'
    | 'ano_fabrico'
    | 'interventions'
    | 'observations'
    | null
  >(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const getPendingEventCounts = (carId: number) => {
    const events = maintenanceEventsByCar[carId] || [];
    const now = Date.now();

    return events.reduce(
      (counts, event) => {
        if (event.stateId === 2) {
          if (!event.isResolved || !event.isRead) counts.observations += 1;
        } else if (!event.endDate || new Date(event.endDate).getTime() >= now) {
          counts.interventions += 1;
        }

        return counts;
      },
      { interventions: 0, observations: 0 }
    );
  };

  // Filter and sort cars
  const filteredAndSortedCars = useMemo(() => {
    if (!carsMaintenance) return [];

    let result = [...carsMaintenance];

    // Multi-term search (e.g., "peugeot 208" or "00-xx-11 2020")
    if (searchQuery) {
      const terms = searchQuery
        .toLowerCase()
        .split(' ')
        .filter(term => term.trim() !== '');
      result = result.filter(car => {
        const carText =
          `${car.carPlate} ${car.marca || ''} ${car.modelo || ''} ${car.ano_fabrico || ''}`.toLowerCase();
        return terms.every(term => carText.includes(term));
      });
    }

    // Sort cars only after a sort option has been selected.
    if (sortBy) {
      result.sort((a, b) => {
        const aCounts = getPendingEventCounts(a.id);
        const bCounts = getPendingEventCounts(b.id);
        const isCountSort =
          sortBy === 'interventions' || sortBy === 'observations';
        const aValue = isCountSort
          ? aCounts[sortBy]
          : sortBy === 'ano_fabrico'
            ? String(a.ano_fabrico || '')
            : (a[sortBy] as string)?.toLowerCase() || '';
        const bValue = isCountSort
          ? bCounts[sortBy]
          : sortBy === 'ano_fabrico'
            ? String(b.ano_fabrico || '')
            : (b[sortBy] as string)?.toLowerCase() || '';

        if (isCountSort) {
          return sortOrder === 'asc'
            ? (aValue as number) - (bValue as number)
            : (bValue as number) - (aValue as number);
        }

        return sortOrder === 'asc'
          ? (aValue as string).localeCompare(bValue as string)
          : (bValue as string).localeCompare(aValue as string);
      });
    }

    return result;
  }, [carsMaintenance, maintenanceEventsByCar, searchQuery, sortBy, sortOrder]);

  const handleCardClick = (matricula: string) => {
    navigate(`/dashboard/carManagement/cardetails/${matricula}`);
  };

  // Toggle sort order (asc ↔ desc)
  const toggleSortOrder = () => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  // Loading state: Only show skeleton cards (2 columns per row)
  if (!carsMaintenance || carsMaintenance.length < 1) {
    return (
      <div className="p-4">
        {/* <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gestão de Veículos</h2>
        </div> */}

        {/* Search and Sort Controls (kept visible) */}
        <Card className="mb-3">
          <Card.Body className="p-3">
            <Row className="g-3 p-1 align-items-center">
              <Col md={5}>
                <InputGroup>
                  <Form.Control type="text" placeholder="Pesquisar" disabled />
                </InputGroup>
              </Col>
              <Col md={7} className="d-flex justify-content-end">
                <div className="d-flex custom-dropdown">
                  <DropdownButton
                    title="Ordenar por"
                    variant="outline-secondary"
                    disabled
                    align="end"
                  >
                    <Dropdown.Item eventKey="carPlate">Matrícula</Dropdown.Item>
                    <Dropdown.Item eventKey="marca">Marca</Dropdown.Item>
                    <Dropdown.Item eventKey="modelo">Modelo</Dropdown.Item>
                    <Dropdown.Item eventKey="ano_fabrico">Ano</Dropdown.Item>
                    <Dropdown.Item eventKey="interventions">
                      Intervenções
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="observations">
                      Observações
                    </Dropdown.Item>
                  </DropdownButton>
                  <Button
                    variant="outline-secondary"
                    disabled
                    className="custom-sort-button"
                  >
                    ▴
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Skeleton Cards (2 columns per row, fills the page) */}
        <Row xs={1} md={2} className="g-3">
          {[...Array(6)].map((_, index) => (
            <Col key={index}>
              <Card
                style={{ cursor: 'pointer', height: '100%' }}
                className="h-100 shadow-sm"
              >
                <Card.Body>
                  <Row className="align-items-center">
                    <Col xs={6}>
                      <SkeletonLoader width="100%" height="1.5rem" />
                    </Col>
                    <Col xs={6}>
                      <SkeletonLoader width="100%" height="1.5rem" />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <>
      <style>{dropdownStyles}</style>

      <div className="p-4">
        {/* <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Gestão de Veículos</h2>
        </div> */}

        {/* Search and Sort Controls */}
        <Card className="mb-3">
          <Card.Body className="p-3">
            <Row className="g-3 p-1 align-items-center">
              {/* Search Bar (5/12 width) */}
              <Col md={5}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Col>

              {/* Sort Controls (7/12 width, connected buttons) */}
              <Col md={7} className="d-flex justify-content-end">
                <div className="d-flex custom-dropdown">
                  <DropdownButton
                    title={
                      sortBy === 'carPlate'
                        ? 'Matrícula'
                        : sortBy === 'marca'
                          ? 'Marca'
                          : sortBy === 'modelo'
                            ? 'Modelo'
                            : sortBy === 'interventions'
                              ? 'Intervenções'
                              : sortBy === 'observations'
                                ? 'Observações'
                                : 'Ordenar por'
                    }
                    variant="outline-secondary"
                    onSelect={eventKey => {
                      const newSortBy = eventKey as
                        | 'carPlate'
                        | 'marca'
                        | 'modelo'
                        | 'ano_fabrico'
                        | 'interventions'
                        | 'observations'
                        | null;
                      setSortBy(newSortBy);
                    }}
                    align="end"
                  >
                    <Dropdown.Item eventKey="carPlate">Matrícula</Dropdown.Item>
                    <Dropdown.Item eventKey="marca">Marca</Dropdown.Item>
                    <Dropdown.Item eventKey="modelo">Modelo</Dropdown.Item>
                    <Dropdown.Item eventKey="ano_fabrico">Ano</Dropdown.Item>
                    <Dropdown.Item eventKey="interventions">
                      Intervenções
                    </Dropdown.Item>
                    <Dropdown.Item eventKey="observations">
                      Observações
                    </Dropdown.Item>
                  </DropdownButton>
                  <Button
                    variant="outline-secondary"
                    onClick={toggleSortOrder}
                    disabled={!sortBy}
                    title={
                      sortOrder === 'asc'
                        ? 'Ordem ascendente'
                        : 'Ordem descendente'
                    }
                    className="custom-sort-button"
                  >
                    {sortOrder === 'asc' ? '▴' : '▾'}
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Car List (2 columns per row) */}
        <Row xs={1} md={3} className="g-3">
          {filteredAndSortedCars.length > 0 ? (
            filteredAndSortedCars.map(car => (
              <Col key={car.guidCarro}>
                <Card
                  onClick={() => handleCardClick(car.guidCarro)}
                  style={{ cursor: 'pointer', height: '100%' }}
                  className="h-100 shadow-sm"
                >
                  <Card.Body className="p-3">
                    <Row className="align-items-center">
                      <Col xs={6}>
                        <div className="fw-bold">{car.carPlate}</div>
                      </Col>
                    </Row>
                    <Row className="align-items-center mt-2">
                      <Col xs={6} className="small text-muted">
                        {(() => {
                          const counts = getPendingEventCounts(car.id);
                          return `${counts.interventions} intervenções · ${counts.observations} observações`;
                        })()}
                      </Col>
                      <Col xs={6}>
                        <div className="small text-muted text-end">
                          {car.marca} {car.modelo} {car.ano_fabrico}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <Alert variant="info" className="text-center py-4">
                Nenhum veículo encontrado.
              </Alert>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};

export default index;
